import { MagicTotpChallenge } from "./MagicTotpChallenge";
import { useAsyncCallback } from "../../hooks/useAsyncCallback";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  useForm,
  type UseFormRegisterReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  confirmSignupSchema,
  forgotPasswordSchema,
  loginPayloadSchema,
  registerPayloadSchema,
  resetPasswordSchema,
} from "@io-spoke/contracts/auth";
import {
  requestMagicLinkSchema,
} from "@io-spoke/contracts/magicLinks";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  completeNewPasswordChallenge,
  confirmSignup,
  login,
  register,
  requestMagicLink,
  requestPasswordReset,
  resendSignupCode,
  resetPassword,
  verifyMagicLink,
  verifyMfaChallenge,
  verifyRecoveryCode,
} from "../../lib/auth";
import { consumePostLoginRedirectPath, setAuthTokens } from "../../lib/authStorage";

export type AuthMode =
  | "login"
  | "signup"
  | "verify"
  | "magic"
  | "mfa"
  | "new_password"
  | "forgot"
  | "reset";

export type AuthChallengeContext = {
  type: "mfa" | "new_password";
  email: string;
  session: string;
  rememberMe: boolean;
};

type FormError = { message?: unknown };

type AuthModeFormProps = {
  mode: AuthMode;
  email: string;
  initialVerificationCode: string;
  initialMagicToken: string;
  challenge: AuthChallengeContext | null;
  notice: string | null;
  onEmailChange: (email: string) => void;
  onNoticeChange: (notice: string | null) => void;
  onModeChange: (mode: AuthMode) => void;
  onChallenge: (challenge: AuthChallengeContext) => void;
  navigate: (route: string) => void;
};

const passwordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "New password must be at least 8 characters."),
    confirmation: z.string().min(1, "Confirm your new password."),
  })
  .refine((value) => value.newPassword === value.confirmation, {
    path: ["confirmation"],
    message: "The passwords do not match.",
  });

const resetFormSchema = resetPasswordSchema
  .extend({
    confirmation: z.string().min(1, "Confirm your new password."),
  })
  .refine((value) => value.newPassword === value.confirmation, {
    path: ["confirmation"],
    message: "The passwords do not match.",
  });

const magicFormSchema = requestMagicLinkSchema.extend({
  email: requestMagicLinkSchema.shape.email.pipe(z.string().email("Enter a valid email address.")),
});

function messageFrom(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function postLoginRoute() {
  return consumePostLoginRedirectPath() ?? "/";
}

function FieldMessage({ id, error }: { id: string; error?: FormError }) {
  return typeof error?.message === "string" ? (
    <p id={id} className="text-sm text-destructive" role="alert">
      {error.message}
    </p>
  ) : null;
}

function FormMessages({
  notice,
  error,
}: {
  notice: string | null;
  error?: FormError;
}) {
  return (
    <>
      {notice ? (
        <p
          className="text-sm text-emerald-700 dark:text-emerald-300"
          role="status"
        >
          {notice}
        </p>
      ) : null}
      {typeof error?.message === "string" ? (
        <p className="text-sm text-destructive" role="alert">
          {error.message}
        </p>
      ) : null}
    </>
  );
}

function EmailField({
  registration,
  error,
}: {
  registration: UseFormRegisterReturn;
  error?: FormError;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor="email">Email</Label>
      <Input
        id="email"
        type="email"
        placeholder="you@company.com"
        autoComplete="email"
        {...registration}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "email-error" : undefined}
      />
      <FieldMessage id="email-error" error={error} />
    </div>
  );
}

function LoginForm(props: AuthModeFormProps) {
  const form = useForm<
    z.input<typeof loginPayloadSchema>,
    unknown,
    z.output<typeof loginPayloadSchema>
  >({
    resolver: zodResolver(loginPayloadSchema),
    defaultValues: { email: props.email, password: "", rememberMe: false },
  });
  const {
    pending: submitPending,
    callback: submit,
  } = useAsyncCallback(async (values: z.output<typeof loginPayloadSchema>) => {
    props.onNoticeChange(null);
    try {
      const result = await login(values);
      if (result.challenge) {
        props.onChallenge({
          type:
            result.challenge.type === "new_password" ? "new_password" : "mfa",
          email: values.email,
          session: result.challenge.session,
          rememberMe: values.rememberMe ?? false,
        });
      } else {
        props.navigate(postLoginRoute());
      }
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to sign in."),
      });
    }
  });
  return (
    <form className="space-y-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <EmailField
        registration={form.register("email", {
          onChange: (event) => props.onEmailChange(event.target.value),
        })}
        error={form.formState.errors.email}
      />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <button
            type="button"
            className="min-h-[44px] px-2 text-xs font-medium text-muted-foreground transition hover:text-foreground"
            onClick={() => props.onModeChange("forgot")}
          >
            Forgot password?
          </button>
        </div>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          placeholder="********"
          {...form.register("password")}
          aria-invalid={Boolean(form.formState.errors.password)}
          aria-describedby={
            form.formState.errors.password ? "password-error" : undefined
          }
        />
        <FieldMessage
          id="password-error"
          error={form.formState.errors.password}
        />
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Controller
            name="rememberMe"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                id="remember-me"
                checked={field.value === true}
                onCheckedChange={(checked) => field.onChange(checked === true)}
              />
            )}
          />
          <Label
            htmlFor="remember-me"
            className="text-xs text-muted-foreground"
          >
            Remember me on this device
          </Label>
        </div>
      </div>
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
      <Button loading={submitPending} loadingLabel="Signing in..."
        className="w-full"
        type="submit"
        disabled={form.formState.isSubmitting}
      >
        {form.formState.isSubmitting ? "Signing in..." : "Sign in"}
      </Button>
    </form>
  );
}

function SignupForm(props: AuthModeFormProps) {
  const form = useForm<
    z.input<typeof registerPayloadSchema>,
    unknown,
    z.output<typeof registerPayloadSchema>
  >({
    resolver: zodResolver(registerPayloadSchema),
    defaultValues: { name: "", email: props.email, password: "" },
  });
  const {
    pending: submitPending,
    callback: submit,
  } = useAsyncCallback(async (values: z.output<typeof registerPayloadSchema>) => {
    props.onNoticeChange(null);
    try {
      await register(values);
      localStorage.setItem("auth.pendingVerificationEmail", values.email);
      props.onEmailChange(values.email);
      props.onModeChange("verify");
      props.onNoticeChange("Check your email for the verification code.");
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to create account."),
      });
    }
  });
  return (
    <form className="space-y-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          autoComplete="name"
          placeholder="Alex Morgan"
          {...form.register("name")}
        />
      </div>
      <EmailField
        registration={form.register("email", {
          onChange: (event) => props.onEmailChange(event.target.value),
        })}
        error={form.formState.errors.email}
      />
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          autoComplete="new-password"
          placeholder="********"
          {...form.register("password")}
          aria-invalid={Boolean(form.formState.errors.password)}
          aria-describedby={
            form.formState.errors.password ? "password-error" : undefined
          }
        />
        <FieldMessage
          id="password-error"
          error={form.formState.errors.password}
        />
      </div>
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
      <Button loading={submitPending} loadingLabel="Creating account..."
        className="w-full"
        type="submit"
        disabled={form.formState.isSubmitting}
      >
        {form.formState.isSubmitting ? "Creating account..." : "Create account"}
      </Button>
    </form>
  );
}

function VerifyForm(props: AuthModeFormProps) {
  const [isResending, setIsResending] = useState(false);
  const form = useForm<
    z.input<typeof confirmSignupSchema>,
    unknown,
    z.output<typeof confirmSignupSchema>
  >({
    resolver: zodResolver(confirmSignupSchema),
    defaultValues: { email: props.email, code: props.initialVerificationCode },
  });
  const {
    pending: submitPending,
    callback: submit,
  } = useAsyncCallback(async (values: z.output<typeof confirmSignupSchema>) => {
    props.onNoticeChange(null);
    try {
      await confirmSignup(values);
      localStorage.removeItem("auth.pendingVerificationEmail");
      props.onModeChange("login");
      props.onNoticeChange("Your account is verified. You can sign in now.");
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to verify account."),
      });
    }
  });
  const {
    pending: resendPending,
    callback: resend,
  } = useAsyncCallback(async () => {
    if (!(await form.trigger("email"))) return;
    form.clearErrors("root");
    props.onNoticeChange(null);
    setIsResending(true);
    try {
      await resendSignupCode({ email: String(form.getValues("email")) });
      props.onNoticeChange("Verification code resent.");
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to resend the code."),
      });
    } finally {
      setIsResending(false);
    }
  });
  return (
    <form className="space-y-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <EmailField
        registration={form.register("email", {
          onChange: (event) => props.onEmailChange(event.target.value),
        })}
        error={form.formState.errors.email}
      />
      <div className="space-y-2">
        <Label htmlFor="verification-code">Verification code</Label>
        <Input
          id="verification-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          {...form.register("code")}
          aria-invalid={Boolean(form.formState.errors.code)}
          aria-describedby={
            form.formState.errors.code ? "verification-code-error" : undefined
          }
        />
        <FieldMessage
          id="verification-code-error"
          error={form.formState.errors.code}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={form.formState.isSubmitting || isResending}
        loading={resendPending} loadingLabel="Sending code…" onClick={() => void resend()}
      >
        {isResending ? "Resending..." : "Resend code"}
      </Button>
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
      <Button loading={submitPending} loadingLabel="Verifying..."
        className="w-full"
        type="submit"
        disabled={form.formState.isSubmitting || isResending}
      >
        {form.formState.isSubmitting ? "Verifying..." : "Verify account"}
      </Button>
    </form>
  );
}

function ForgotForm(props: AuthModeFormProps) {
  const form = useForm<
    z.input<typeof forgotPasswordSchema>,
    unknown,
    z.output<typeof forgotPasswordSchema>
  >({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: props.email },
  });
  const {
    pending: submitPending,
    callback: submit,
  } = useAsyncCallback(async (values: z.output<typeof forgotPasswordSchema>) => {
    props.onNoticeChange(null);
    try {
      await requestPasswordReset(values);
      props.onEmailChange(values.email);
      props.onModeChange("reset");
      props.onNoticeChange(
        "If that account exists, a password reset code is on its way.",
      );
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to request a password reset."),
      });
    }
  });
  return (
    <form className="space-y-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <EmailField
        registration={form.register("email", {
          onChange: (event) => props.onEmailChange(event.target.value),
        })}
        error={form.formState.errors.email}
      />
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
      <Button loading={submitPending} loadingLabel="Sending code..."
        className="w-full"
        type="submit"
        disabled={form.formState.isSubmitting}
      >
        {form.formState.isSubmitting ? "Sending code..." : "Send reset code"}
      </Button>
    </form>
  );
}

function PasswordFields({
  codeRegistration,
  newPasswordRegistration,
  confirmationRegistration,
  errors,
}: {
  codeRegistration?: UseFormRegisterReturn;
  newPasswordRegistration: UseFormRegisterReturn;
  confirmationRegistration: UseFormRegisterReturn;
  errors: {
    code?: FormError;
    newPassword?: FormError;
    confirmation?: FormError;
  };
}) {
  return (
    <div className="space-y-2">
      {codeRegistration ? (
        <>
          <Label htmlFor="verification-code">Verification code</Label>
          <Input
            id="verification-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            {...codeRegistration}
            aria-invalid={Boolean(errors.code)}
            aria-describedby={
              errors.code ? "verification-code-error" : undefined
            }
          />
          <FieldMessage id="verification-code-error" error={errors.code} />
        </>
      ) : null}
      <Label htmlFor="new-password">New password</Label>
      <Input
        id="new-password"
        type="password"
        autoComplete="new-password"
        {...newPasswordRegistration}
        aria-invalid={Boolean(errors.newPassword)}
        aria-describedby={
          errors.newPassword ? "new-password-error" : "new-password-help"
        }
      />
      <p id="new-password-help" className="text-xs text-muted-foreground">
        Use at least 8 characters and follow your organization’s password
        requirements.
      </p>
      <FieldMessage id="new-password-error" error={errors.newPassword} />
      <Label htmlFor="confirm-new-password">Confirm new password</Label>
      <Input
        id="confirm-new-password"
        type="password"
        autoComplete="new-password"
        {...confirmationRegistration}
        aria-invalid={Boolean(errors.confirmation)}
        aria-describedby={
          errors.confirmation ? "confirm-new-password-error" : undefined
        }
      />
      <FieldMessage
        id="confirm-new-password-error"
        error={errors.confirmation}
      />
    </div>
  );
}

function ResetForm(props: AuthModeFormProps) {
  type Input = z.input<typeof resetFormSchema>;
  type Output = z.output<typeof resetFormSchema>;
  const form = useForm<Input, unknown, Output>({
    resolver: zodResolver(resetFormSchema),
    defaultValues: {
      email: props.email,
      code: "",
      newPassword: "",
      confirmation: "",
    },
  });
  const {
    pending: submitPending,
    callback: submit,
  } = useAsyncCallback(async ({ confirmation: _confirmation, ...values }: Output) => {
    props.onNoticeChange(null);
    try {
      await resetPassword(values);
      props.onModeChange("login");
      props.onNoticeChange("Password updated. You can sign in now.");
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to update password."),
      });
    }
  });
  return (
    <form className="space-y-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <EmailField
        registration={form.register("email", {
          onChange: (event) => props.onEmailChange(event.target.value),
        })}
        error={form.formState.errors.email}
      />
      <PasswordFields
        codeRegistration={form.register("code")}
        newPasswordRegistration={form.register("newPassword")}
        confirmationRegistration={form.register("confirmation")}
        errors={form.formState.errors}
      />
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
      <Button loading={submitPending} loadingLabel="Resetting password…"
        className="w-full"
        type="submit"
        disabled={form.formState.isSubmitting}
      >
        {form.formState.isSubmitting
          ? "Updating password..."
          : "Update password"}
      </Button>
    </form>
  );
}

function NewPasswordForm(props: AuthModeFormProps) {
  type Input = z.input<typeof passwordSchema>;
  type Output = z.output<typeof passwordSchema>;
  const form = useForm<Input, unknown, Output>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { newPassword: "", confirmation: "" },
  });
  const {
    pending: submitPending,
    callback: submit,
  } = useAsyncCallback(async (values: Output) => {
    props.onNoticeChange(null);
    if (!props.challenge?.session) {
      form.setError("root", {
        message: "Your sign-in session expired. Please sign in again.",
      });
      return;
    }
    try {
      await completeNewPasswordChallenge({
        email: props.challenge.email,
        newPassword: values.newPassword,
        session: props.challenge.session,
        rememberMe: props.challenge.rememberMe,
      });
      props.onNoticeChange("Password updated. You are now signed in.");
      props.navigate(postLoginRoute());
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to update password."),
      });
    }
  });
  return (
    <form className="space-y-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <PasswordFields
        newPasswordRegistration={form.register("newPassword")}
        confirmationRegistration={form.register("confirmation")}
        errors={form.formState.errors}
      />
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
      <Button loading={submitPending} loadingLabel="Updating password…"
        className="w-full"
        type="submit"
        disabled={form.formState.isSubmitting}
      >
        {form.formState.isSubmitting
          ? "Updating password..."
          : "Set password and sign in"}
      </Button>
    </form>
  );
}

function MfaForm(props: AuthModeFormProps) {
  const [useRecovery, setUseRecovery] = useState(false);
  const schema = useMemo(
    () =>
      z.object({
        code: z
          .string()
          .trim()
          .min(
            1,
            useRecovery
              ? "Recovery code is required."
              : "Verification code is required.",
          ),
      }),
    [useRecovery],
  );
  const form = useForm<
    z.input<typeof schema>,
    unknown,
    z.output<typeof schema>
  >({ resolver: zodResolver(schema), defaultValues: { code: "" } });
  const toggle = () => {
    setUseRecovery((value) => !value);
    form.resetField("code");
    form.clearErrors();
  };
  const {
    pending: submitPending,
    callback: submit,
  } = useAsyncCallback(async ({ code }: z.output<typeof schema>) => {
    props.onNoticeChange(null);
    if (!props.challenge?.session) {
      form.setError("root", {
        message: "Missing MFA session. Please sign in again.",
      });
      return;
    }
    try {
      if (useRecovery)
        await verifyRecoveryCode({
          email: props.challenge.email,
          code,
          rememberMe: props.challenge.rememberMe,
        });
      else
        await verifyMfaChallenge({
          email: props.challenge.email,
          code,
          session: props.challenge.session,
          rememberMe: props.challenge.rememberMe,
        });
      props.navigate(postLoginRoute());
    } catch (error) {
      form.setError("root", {
        message: messageFrom(error, "Unable to verify code."),
      });
    }
  });
  return (
    <form className="space-y-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="mfa-code">
            {useRecovery ? "Recovery code" : "Authenticator code"}
          </Label>
          <button
            type="button"
            className="min-h-[44px] px-2 text-xs font-medium text-muted-foreground transition hover:text-foreground"
            onClick={toggle}
          >
            {useRecovery ? "Use authenticator code" : "Use recovery code"}
          </button>
        </div>
        <Input
          id="mfa-code"
          placeholder={useRecovery ? "recovery-code" : "123456"}
          inputMode={useRecovery ? "text" : "numeric"}
          {...form.register("code")}
          aria-invalid={Boolean(form.formState.errors.code)}
          aria-describedby={
            form.formState.errors.code ? "mfa-code-error" : undefined
          }
        />
        <FieldMessage id="mfa-code-error" error={form.formState.errors.code} />
      </div>
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
      <Button loading={submitPending} loadingLabel="Verifying code…"
        className="w-full"
        type="submit"
        disabled={form.formState.isSubmitting}
      >
        {form.formState.isSubmitting
          ? "Verifying code..."
          : useRecovery
            ? "Use recovery code"
            : "Verify code"}
      </Button>
    </form>
  );
}

function MagicForm(props: AuthModeFormProps) {
  const [challenge, setChallenge] = useState<{ session: string; expiresAt: string } | null>(null);
  const requestVersion = useRef(0);
  const mounted = useRef(true);
  const callbacks = useRef(props);
  callbacks.current = props;
  const automatic = useRef<{ token: string; promise: ReturnType<typeof verifyMagicLink> } | null>(null);
  useEffect(() => {
    mounted.current = true;
    const changed = () => { requestVersion.current += 1; setChallenge(null); setIsAutoVerifying(false); };
    window.addEventListener("auth:changed", changed);
    return () => { mounted.current = false; window.removeEventListener("auth:changed", changed); };
  }, []);
  const acceptResult = (result: Awaited<ReturnType<typeof verifyMagicLink>>, version: number) => {
    if (!mounted.current || requestVersion.current !== version) return;
    setIsAutoVerifying(false);
    if ("challenge" in result) { setChallenge(result.challenge); return; }
    setAuthTokens({ accessToken: result.accessToken, user: result.user }, false);
    callbacks.current.onNoticeChange("Signed in with your magic link.");
    callbacks.current.navigate(postLoginRoute());
  };
  const [isAutoVerifying, setIsAutoVerifying] = useState(false);
  const form = useForm<
    z.input<typeof magicFormSchema>,
    unknown,
    z.output<typeof magicFormSchema>
  >({
    resolver: zodResolver(magicFormSchema),
    defaultValues: { email: props.email },
  });
  const { initialMagicToken } = props;
  useEffect(() => {
    const token = initialMagicToken.trim();
    const version = ++requestVersion.current;
    setChallenge(null);
    if (!token) return;
    setIsAutoVerifying(true);
    if (automatic.current?.token !== token) automatic.current = { token, promise: verifyMagicLink({ token }) };
    automatic.current.promise
      .then((result) => acceptResult(result, version))
      .catch((error) => {
        if (mounted.current && requestVersion.current === version) form.setError("root", { message: messageFrom(error, "Unable to verify magic link.") });
      })
      .finally(() => { if (mounted.current && requestVersion.current === version) setIsAutoVerifying(false); });
    return () => { requestVersion.current += 1; };
  }, [initialMagicToken]);
  const { pending: requestPending, callback: request } = useAsyncCallback(form.handleSubmit(async ({ email }) => {
    const version = ++requestVersion.current;
    props.onNoticeChange(null);
    try {
      await requestMagicLink({ email });
      if (mounted.current && requestVersion.current === version) props.onNoticeChange("Magic link requested. Check your email.");
    } catch (error) {
      if (mounted.current && requestVersion.current === version) form.setError("root", { message: messageFrom(error, "Unable to request magic link.") });
    }
  }));
  const pending = requestPending || isAutoVerifying;
  if (challenge) {
    return <MagicTotpChallenge key={challenge.session} challenge={challenge}
      onComplete={() => {
        setChallenge(null);
        props.onNoticeChange("Signed in with your email link and authenticator.");
        props.navigate(postLoginRoute());
      }}
      onBack={() => {
        requestVersion.current += 1;
        setChallenge(null); form.clearErrors();
        props.onNoticeChange(null);
      }} />;
  }

  return (
    <form className="space-y-5" onSubmit={request} noValidate>
      <EmailField
        registration={form.register("email", { onChange: (event) => props.onEmailChange(event.target.value) })}
        error={form.formState.errors.email}
      />
      <Button type="submit" className="w-full" disabled={pending} loading={pending}
        loadingLabel={isAutoVerifying ? "Verifying magic link…" : "Sending magic link…"}>
        Send magic link
      </Button>
      <FormMessages notice={props.notice} error={form.formState.errors.root} />
    </form>
  );
}

export function AuthModeForm(props: AuthModeFormProps) {
  switch (props.mode) {
    case "login":
      return <LoginForm {...props} />;
    case "signup":
      return <SignupForm {...props} />;
    case "verify":
      return <VerifyForm {...props} />;
    case "forgot":
      return <ForgotForm {...props} />;
    case "reset":
      return <ResetForm {...props} />;
    case "magic":
      return <MagicForm {...props} />;
    case "mfa":
      return <MfaForm {...props} />;
    case "new_password":
      return <NewPasswordForm {...props} />;
  }
}
